import { scrollHelper } from '@/services/store/schedule/config/listeners/schedule';

const fnQueue: (() => void)[] = [];
const operationsQueue: ComputedRef<boolean>[] = [];

let retryTimeoutId: number | null = null;

/**
 * Composable for queueing functions when schedule operations are in progress.
 * Monitors preset changes, capacity toggling, and scrolling state.
 */
export function useScheduleOperationsQueue() {
  const registerOperation = (operation: ComputedRef<boolean>): void => {
    operationsQueue.push(operation);
  };

  const isAnyOperationInProgress = (): boolean => {
    return scrollHelper.isScrolling || operationsQueue.some((operation) => operation.value);
  };

  const executeQueue = (): void => {
    if (isAnyOperationInProgress()) {
      // If operations are still in progress and we have queued functions, schedule another retry
      if (fnQueue.length > 0) {
        scheduleRetry();
      }
      return;
    }

    if (fnQueue.length > 0) {
      fnQueue.forEach((fn) => fn());
      fnQueue.length = 0;
    }
  };

  const scheduleRetry = (): void => {
    if (retryTimeoutId !== null) {
      clearTimeout(retryTimeoutId);
    }

    retryTimeoutId = window.setTimeout(() => {
      retryTimeoutId = null;
      executeQueue();
    }, 100);
  };

  const queueFunction = (fn: () => void): void => {
    if (isAnyOperationInProgress()) {
      fnQueue.push(fn);
      scheduleRetry();
    } else {
      fn();
    }
  };

  const cleanup = (): void => {
    if (retryTimeoutId !== null) {
      clearTimeout(retryTimeoutId);
      retryTimeoutId = null;
    }

    fnQueue.length = 0;
    operationsQueue.length = 0;
  };

  return {
    queueFunction,
    registerOperation,
    cleanup,
  };
}
